import { addDays, getDate, lastDayOfMonth, startOfDay } from 'date-fns'

import { LABELS, UNITS } from '@/constants/common/message.constants'

export type YearMonth = {
  year: number
  month: number
}

export type Cycle = {
  start: Date
  end: Date
}

// その月に渡された日付が存在しない場合に月末を返す
export function clampDate(year: number, month: number, day: number): Date {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(day, lastDay))
}

// 日付オブジェクトの翌日を返す
export function nextDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
}

// 前月の { year, month } を返す
export function prevMonth(year: number, month: number): YearMonth {
  if (month === 0) return { year: year - 1, month: 11 }
  return { year, month: month - 1 }
}

// 翌月の { year, month } を返す
export function nextMonth(year: number, month: number): YearMonth {
  if (month === 11) return { year: year + 1, month: 0 }
  return { year, month: month + 1 }
}

// today と closingDay から、締日サイクルの開始・終了を返す
export function calculateCycle(today: Date, closingDay: number | null): Cycle {
  const year = today.getFullYear()
  const month = today.getMonth()
  const day = today.getDate()

  let start: Date, end: Date

  if (!closingDay) {
    start = new Date(year, month, 1)
    end = new Date(year, month + 1, 0)
    return { start, end }
  }

  if (day <= closingDay) {
    const prevYearMonth = prevMonth(year, month)
    start = nextDay(
      clampDate(prevYearMonth.year, prevYearMonth.month, closingDay)
    )
    end = clampDate(year, month, closingDay)
  } else {
    start = nextDay(clampDate(year, month, closingDay))
    const nextYearMonth = nextMonth(year, month)
    end = clampDate(nextYearMonth.year, nextYearMonth.month, closingDay)
  }

  return { start, end }
}

/**
 * cycleStartDate は closingDay を基に計算された
 * 「サイクル開始日」であることを前提とする
 */
export function calculateCycleFromStartDate(
  cycleStartDate: Date,
  closingDay: number
): { start: Date; end: Date } {
  const start = cycleStartDate

  const year = start.getFullYear()
  const month = start.getMonth()

  // 解決締日が月末に着地したサイクルは開始日が翌月 1 日になる（getNewEffectiveDate）。
  // このとき終了日は開始と同じ月の締日（月末超過は clampDate が補正。締日 31 = 常に当月末で
  // 従来の 31 特別分岐を包含）。開始日の日にちが締日より後（通常サイクル: 開始 = 締日 + 1 日）の
  // ときだけ翌月の締日で締まる。VOC_PJ-939 #4387 で締日 28/29/30 が選択可能になり、
  // 31 以外でも月末着地が発生するようになった
  const end =
    start.getDate() <= closingDay
      ? clampDate(year, month, closingDay)
      : clampDate(month === 11 ? year + 1 : year, (month + 1) % 12, closingDay)

  return { start, end }
}

// 締日を受け取り、締日表示用の文字列を返す
export function getClosingDateDisplay(closingDate?: number | null): string {
  return !closingDate || closingDate === 31
    ? LABELS.END_OF_MONTH
    : `${closingDate}${UNITS.DATETIME.DAY}`
}

// 開始日の調整（締日が月末に変更かつ、処理中のレポートの年月の末日が31日の場合）
export function clampStartDate(year: number, month: number, day: number): Date {
  const lastDay = new Date(year, month + 1, 0).getDate()
  if (day > lastDay) {
    return new Date(year, month + 1, 1)
  }
  return new Date(year, month, day)
}

// 終了日の調整
export function clampEndDate(year: number, month: number, day: number): Date {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(day, lastDay))
}

/**
 * 各月次レポートの期間を計算するメソッド
 *
 * 締日変更時に特殊処理が必要な3つのパターン:
 *
 * 1. 締日増加（例: 5日 -> 25日、適用開始日=8/6）
 *    - 8月レポート: 7/6-8/5（通常）
 *    - 9月レポート: 8/6-9/25（特殊: 浮き期間8/6-8/25を含む）
 *
 * 2. 締日減少（例: 25日 -> 5日、適用開始日=7/26）
 *    - 7月レポート: 6/26-7/25（通常）
 *    - 8月レポート: 7/26-8/5（特殊: 短縮期間）
 *    - 9月レポート: 8/6-9/5（通常）
 *
 * 3. 締日減少かつ適用開始日が月初（例: 31日 -> 5日、適用開始日=8/1）
 *    - 7月レポート: 7/1-7/31（通常）
 *    - 8月レポート: 8/1-8/5（特殊: 短縮期間）
 *    - 9月レポート: 8/6-9/5（通常）
 */
export function calculateReportPeriod(
  operatingYearMonth: Date,
  closingDate: number,
  oldClosingDate?: number,
  closingDateEffectiveFrom?: Date
): { startDate: Date; endDate: Date } {
  const year = operatingYearMonth.getFullYear()
  const month = operatingYearMonth.getMonth() + 1

  // 締日変更がある場合の特別処理
  if (
    oldClosingDate &&
    closingDateEffectiveFrom &&
    oldClosingDate !== closingDate
  ) {
    const effMonth = closingDateEffectiveFrom.getMonth() + 1
    const effDay = closingDateEffectiveFrom.getDate()

    // 締日増加の場合: 浮き期間を含む新サイクル
    if (closingDate > oldClosingDate && month === effMonth + 1) {
      return {
        startDate: closingDateEffectiveFrom,
        endDate: clampEndDate(year, month - 1, closingDate)
      }
    }

    // 締日減少の場合: 統合期間の判定
    if (closingDate < oldClosingDate) {
      // パターン1: 適用開始日が月初の場合（31→5、8/1適用など）
      if (month === effMonth && effDay === 1) {
        return {
          startDate: closingDateEffectiveFrom,
          endDate: clampEndDate(year, month - 1, closingDate)
        }
      }

      // パターン2: 適用開始日が月中の場合（25→15、7/26適用など）
      if (month === effMonth + 1 && effDay > 1) {
        return {
          startDate: closingDateEffectiveFrom,
          endDate: clampEndDate(year, month - 1, closingDate)
        }
      }
    }
  }

  // 通常の締日計算
  return {
    startDate: clampStartDate(year, month - 2, closingDate + 1),
    endDate: clampEndDate(year, month - 1, closingDate)
  }
}

/**
 * 選択された締日から「新締日適用開始日」を計算して返す
 *
 * @param selectedClosingDate 選択された締日（日付）
 * @returns 新締日適用開始日（Dateオブジェクト）
 *
 * 例：
 * 選択された締日が「10日」の場合、
 * 本日が「12月01日〜12月10日」ならば、「12月11日」を返す
 * 本日が「12月11日〜12月31日」ならば、「1月11日」を返す
 */
// now はテストから基準日を固定するための注入口（本番呼び出しは常に現在時刻）
export function getNewEffectiveDate(
  selectedClosingDate: number,
  now: Date = new Date()
): Date {
  const today = getDate(now) // 今日の日付（例：1/5 ⇨ 5）

  const base = startOfDay(now)

  // 選択された「締日」が本日より前の場合、翌月扱いにする
  // （月初に落としてから月を進める。本日が 29〜31 日のとき setMonth が
  //  存在しない日付経由で翌々月へオーバーフローするのを防ぐ）
  if (today > selectedClosingDate) {
    base.setDate(1)
    base.setMonth(base.getMonth() + 1)
  }

  // 月末を超えないように締日を補正
  const lastDay = getDate(lastDayOfMonth(base))
  const closingDate = Math.min(selectedClosingDate, lastDay)

  base.setDate(closingDate)

  // 締日の翌日
  return addDays(base, 1)
}
